import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import { InferType } from 'yup';
import { editPatientSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditPatientAdditionalInformationMutation,
  usePatientAdditionalInformationQuery,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';

type IFormShape = InferType<typeof editPatientSchema>;

const initialValues: IFormShape = {
  emr_id: '',
  salesforce_id: '',
  billing_id: '',
  care_connect_id: '',
  care_connect_link: '',
  cms_iso_id: '',
  canvas_id: '',
  customer_id: '',
};

export const useEditAdditionalInformation = (patient_id: string) => {
  const patientAdditionalInformation = usePatientAdditionalInformationQuery({
    patient_id,
  });
  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(editPatientSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    reset,
  } = formMethods;

  const { mutateAsync, isPending } =
    useEditPatientAdditionalInformationMutation({
      patient_id,
    });
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOnClick = () => {
    setIsOpen(true);

    const d = patientAdditionalInformation.data;
    if (d) {
      reset({
        billing_id: d.billing_id ?? '',
        care_connect_id: d.care_connect_id ?? '',
        care_connect_link: d.care_connect_link ?? '',
        emr_id: d.emr_id ?? '',
        salesforce_id: d.salesforce_id ?? '',
        canvas_id: d.canvas_id ?? '',
        cms_iso_id: d.cms_iso_id ?? '',
        customer_id: d.customer_id ?? '',
      });
    }
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({
        billing_id: values.billing_id ?? null,
        cms_iso_id: values.cms_iso_id ?? null,
        customer_id: values.customer_id ?? null,
      });

      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset(values);
      toast.success('Patient edited successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  useEffect(() => {
    if (patientAdditionalInformation.data) {
      const {
        billing_id,
        care_connect_id,
        care_connect_link,
        emr_id,
        salesforce_id,
        cms_iso_id,
        canvas_id,
        customer_id,
      } = patientAdditionalInformation.data;

      reset({
        billing_id: billing_id ?? '',
        care_connect_id: care_connect_id ?? '',
        care_connect_link: care_connect_link ?? '',
        emr_id: emr_id ?? '',
        salesforce_id: salesforce_id ?? '',
        canvas_id: canvas_id ?? '',
        cms_iso_id: cms_iso_id ?? '',
        customer_id: customer_id ?? '',
      });
    }
  }, [patientAdditionalInformation.data]);

  return {
    formMethods,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
